import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { ArrowRight } from 'lucide-react'
import MarketingNav from './MarketingNav'
import MediaSlot from './MediaSlot'
import SiteFooter from './SiteFooter'
import './marketing-dark.css'
import { groupByCluster, slugFor, clusterFor } from './marketingMeta'
import { CATALOG, byKey } from '../lib/serviceCatalog'
import { getSession } from '../lib/session'
import { cartCount } from '../lib/cart'

/**
 * ProductsIndex — the public /products index for ai.reright.io.
 *
 * Apple-calm, media-forward, and fully HEADLESS: services + live prices come
 * from GET /api/v1/site/catalog. The fetch NEVER throws — if it fails we fall
 * back to the built-in frontend CATALOG so the page always renders.
 *
 * Reuses the shared shell exactly: <MarketingNav> (with its full mega-dropdown),
 * <MediaSlot> for every media area, and <SiteFooter> — so the whole site reads
 * as one coherent system with the redesigned homepage.
 */

// Built-in fallback — the same fourteen services, enriched with slug + cluster
// from marketingMeta so the page is complete even when the API is cold.
const DEFAULT_SERVICES = CATALOG.map((s) => ({
  key: s.key,
  slug: slugFor(s.key),
  cluster: clusterFor(s.key),
  keyword: s.name,
  name: s.name,
  short: s.short,
  price: s.price,
  icon: s.icon,
  heroImage: '',
  heroVideo: '',
}))

// One-line taglines per cluster for the section heads (mirrors the mega-nav).
const CLUSTER_BLURB = {
  Answer: 'Support and knowledge that answers from your content — never invented.',
  Build: 'APIs, automations, and agents that run over your knowledge.',
  Sell: 'Turn conversations into orders, right inside the chat.',
  Grow: 'Marketing intelligence with the evidence attached.',
  Resell: 'Run the whole platform under your own brand.',
}

export default function ProductsIndex() {
  const navigate = useNavigate()
  const authed = !!getSession()?.apiKey

  const [services, setServices] = useState(DEFAULT_SERVICES)
  const [meta, setMeta] = useState({})
  const [seatCount, setSeatCount] = useState(cartCount())

  // Live cart badge sync.
  useEffect(() => {
    const sync = () => setSeatCount(cartCount())
    sync()
    window.addEventListener('reright-cart', sync)
    return () => window.removeEventListener('reright-cart', sync)
  }, [])

  // Headless catalog + live prices. Never throws — defaults stand on failure.
  useEffect(() => {
    let alive = true
    axios.get('/api/v1/site/catalog')
      .then(({ data }) => {
        if (!alive || !data) return
        if (Array.isArray(data.services) && data.services.length) {
          // Enrich API rows with the canonical icon by key (the API carries no
          // icon) so the cards + mega-nav stay visually consistent.
          setServices(data.services.map((s) => ({ ...s, icon: byKey(s.key)?.icon })))
        }
        if (data.meta && typeof data.meta === 'object') setMeta(data.meta)
      })
      .catch(() => { /* keep defaults */ })
    return () => { alive = false }
  }, [])

  const groups = groupByCluster(services)
  const total = services.length

  const goCart = useCallback(() => navigate('/cart'), [navigate])

  const signIn = { to: authed ? '/' : '/signin', label: authed ? 'Open console' : 'Sign in', router: true }

  return (
    <div className="mkt min-h-screen bg-gray-50 text-gray-900 antialiased">
      <MarketingNav
        services={services}
        cartCount={seatCount}
        onOpenCart={goCart}
        signIn={signIn}
      />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="bg-gray-50 border-b border-gray-200 overflow-hidden">
        <div className="max-w-4xl mx-auto px-5 pt-20 sm:pt-28 pb-14 text-center">
          <Eyebrow center>One platform · {total} services · five lanes</Eyebrow>
          <h1 className="text-[2.6rem] sm:text-6xl lg:text-[4.2rem] font-display font-black tracking-tight leading-[1.02] mt-5 text-gray-900" style={{ textWrap: 'balance' }}>
            {total} services. One platform.
          </h1>
          <p className="text-[17px] sm:text-xl text-gray-500 mt-6 max-w-2xl mx-auto leading-relaxed">
            {meta.intro || 'Every service runs on one thing — your content. Bring it once, then switch on the pieces you need: answer questions, automate work, sell in chat, grow traffic, or resell the whole thing. You pay only for what you turn on.'}
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Cta href="#answer" variant="primary" onHash={scrollToId}>Explore services</Cta>
            <Cta href="/guide" variant="secondary">Read the guide</Cta>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-5 pb-20 sm:pb-24">
          <MediaSlot
            image={meta.heroImage || ''}
            video={meta.heroVideo || ''}
            kind="video"
            label="Platform overview"
            sublabel="A 60-second tour of the platform · add a video in the CMS"
          />
        </div>
      </section>

      {/* ── Services, grouped by the five clusters (alternating sections) ──── */}
      {groups.map((g, i) => {
        const alt = i % 2 === 1
        return (
          <section
            key={g.name}
            id={g.name.toLowerCase()}
            className={`scroll-mt-16 border-b border-gray-200 ${alt ? 'bg-gray-100' : 'bg-gray-50'}`}
          >
            <div className="max-w-6xl mx-auto px-5 py-20 sm:py-24">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                  <Eyebrow>{g.name}</Eyebrow>
                  <h2 className="text-3xl sm:text-4xl font-display font-black tracking-tight text-gray-900 mt-3" style={{ textWrap: 'balance' }}>
                    {g.tagline}
                  </h2>
                  <p className="text-[16px] text-gray-500 mt-3 leading-relaxed max-w-2xl">{CLUSTER_BLURB[g.name] || ''}</p>
                </div>
                <span className="hidden sm:inline-flex shrink-0 text-[13px] font-semibold text-gray-400 tabular-nums">
                  {g.items.length} service{g.items.length === 1 ? '' : 's'}
                </span>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
                {g.items.map((s) => (
                  <ProductCard key={s.key} s={s} alt={alt} />
                ))}
              </div>
            </div>
          </section>
        )
      })}

      {/* ── Closing CTA band ──────────────────────────────────────────────── */}
      <section className="bg-gray-50">
        <div className="max-w-6xl mx-auto px-5 py-24 text-center">
          <h2 className="text-3xl sm:text-5xl font-display font-black tracking-tight text-gray-900" style={{ textWrap: 'balance' }}>
            Turn your website into AI services.
          </h2>
          <p className="text-[17px] sm:text-xl text-gray-500 mt-4 max-w-2xl mx-auto">
            Bring your content, switch on what you need, pay only for what you turn on.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Cta href="#answer" variant="primary" onHash={scrollToId}>Explore services</Cta>
            <Cta href="/guide" variant="secondary">Read the guide</Cta>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  )
}

// ── Premium service card (links to /products/<slug>) ─────────────────────────
function ProductCard({ s, alt }) {
  const Icon = s.icon
  const cardBg = alt ? 'bg-gray-50' : 'bg-gray-100'
  return (
    <a
      href={`/products/${s.slug}`}
      className={`group relative flex flex-col text-left ${cardBg} rounded-2xl border border-gray-200 p-6 hover:border-gray-300 hover:shadow-[0_16px_40px_-24px_rgba(15,23,42,0.35)] hover:-translate-y-0.5 transition-all duration-200`}
    >
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-xl bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/15 transition-colors">
          {Icon
            ? <Icon className="text-primary-600" size={21} />
            : <span className="text-primary-600 font-display font-black text-lg">{(s.name || '?').charAt(0)}</span>}
        </div>
        <span className="text-[15px] font-semibold text-gray-900 tabular-nums">{Number(s.price) === 0 ? 'Free' : <>${s.price}<span className="text-gray-400 font-normal text-[13px]">/mo</span></>}</span>
      </div>
      <h3 className="font-bold text-gray-900 mt-4 text-[17px] tracking-tight">{s.name}</h3>
      <p className="text-[13.5px] text-gray-500 mt-1.5 leading-relaxed flex-1">{s.short}</p>
      <span className="mt-4 text-[13px] font-semibold inline-flex items-center gap-1 text-primary-600">
        View <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
      </span>
    </a>
  )
}

// ── Small shared pieces (mirror Landing.jsx's visual language) ───────────────
function scrollToId(href) {
  const el = typeof href === 'string' && href.startsWith('#') ? document.querySelector(href) : null
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function Eyebrow({ children, center }) {
  return (
    <p className={`inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-primary-600 ${center ? 'justify-center' : ''}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-primary-600" aria-hidden="true" />{children}
    </p>
  )
}

function Cta({ href = '#', variant = 'primary', onHash, children }) {
  const base = 'inline-flex items-center justify-center gap-1.5 rounded-full text-[15px] font-semibold px-6 py-3 transition-all duration-200'
  const style = variant === 'primary'
    ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm hover:shadow'
    : 'border border-gray-300 text-gray-900 hover:border-gray-400 hover:bg-gray-100'
  const cls = `${base} ${style}`
  if (typeof href === 'string' && href.startsWith('#')) {
    return <a href={href} className={cls} onClick={(e) => { e.preventDefault(); onHash && onHash(href) }}>{children}</a>
  }
  return <a href={href} className={cls}>{children}</a>
}
